"use client";

import { useEffect } from "react";
import {
  subscribeToPush,
  showLocalNotification,
} from "@/lib/pushNotifications";

/**
 * RegisterSW — Client-side registration of the UniUI PWA service worker.
 * Handles_update_events, offline/online states, push_subscription, and
 * PWA re-engagement welcome notifications.
 *
 * Rendered once in the root layout (renders nothing to the DOM).
 */
const SW_VERSION = "v14";

// Re-engagement messages shown as local notifications when the user opens
// the PWA. Rotated daily so users see variety.
const RE_ENGAGEMENT_MESSAGES = [
  {
    title: "📚 New Study Material Available!",
    body: "Fresh past questions and solutions have been uploaded. Check them out!",
    url: "/past-questions",
  },
  {
    title: "🎯 Daily AI Credit Bonus",
    body: "5 free AI question credits are waiting for you. Solve more, earn more!",
    url: "/learn",
  },
  {
    title: "🔥 Community is Buzzing!",
    body: "Students in your faculty are discussing hot topics. Join the conversation!",
    url: "/community",
  },
  {
    title: "⚡ New Update is Live",
    body: "UniUI just got better — faster AI, richer profiles, and new study tools.",
    url: "/",
  },
  {
    title: "🏆 Leaderboard Update",
    body: "Check your rank on this week's top contributors board!",
    url: "/leaderboard",
  },
];

function getTodaysMessage() {
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) /
      86400000,
  );
  return RE_ENGAGEMENT_MESSAGES[dayOfYear % RE_ENGAGEMENT_MESSAGES.length];
}

async function maybeShowReEngagement() {
  try {
    const key = "uniui_last_re_engage";
    const last = parseInt(localStorage.getItem(key) || "0", 10);
    const now = Date.now();
    // Show at most once every 6 hours
    if (now - last < 6 * 60 * 60 * 1000) return;
    localStorage.setItem(key, String(now));

    const msg = getTodaysMessage();
    await showLocalNotification(msg.title, msg.body, msg.url);
  } catch {
    // Non-fatal — localStorage_or_notifications_may_be_unavailable
  }
}

// Module-level guard to prevent duplicate push subscription attempts within
// the same page session. RegisterSW calls tryPushSubscribe on both initial load
// AND on every visibilitychange, which causes redundant backend registration.
let pushSubscribed = false;

async function tryPushSubscribe() {
  // Guard against duplicate push subscription attempts within the same session.
  // RegisterSW calls this on both initial load AND on every visibilitychange,
  // which causes redundant backend registration calls. The module-level flag
  // ensures we only attempt once per page session.
  if (pushSubscribed) return;

  // Optimistic lock: mark in-flight immediately so overlapping calls from
  // load/visibility timers cannot start a second subscription flow.
  pushSubscribed = true;
  try {
    let authToken = "";

    const customTokens = localStorage.getItem("uni-ui-auth-tokens");
    if (customTokens) {
      try {
        const parsed = JSON.parse(customTokens);
        authToken = parsed?.access_token ? `Bearer ${parsed.access_token}` : "";
      } catch {
        // ignore malformed custom token
      }
    }

    if (!authToken) {
      const sessionStr =
        localStorage.getItem("sb-auth-token") ||
        localStorage.getItem("supabase.auth.token") ||
        "";
      if (sessionStr) {
        try {
          const parsed = JSON.parse(sessionStr);
          authToken = parsed?.currentSession?.access_token
            ? `Bearer ${parsed.currentSession.access_token}`
            : "";
        } catch {
          authToken = sessionStr.startsWith("Bearer ") ? sessionStr : "";
        }
      }
    }

    if (!authToken) return;

    const subscribed = await subscribeToPush(authToken);
    if (subscribed) {
      await maybeShowReEngagement();
    }
  } catch {
    // Fail silently — push_is_an_enhancement, not_critical
  }
}

export default function RegisterSW() {
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator))
      return;

    const handleLoad = () => {
      // Register / update the service worker
      navigator.serviceWorker
        .register(`/sw.js?${SW_VERSION}`, { scope: "/" })
        .then((registration) => {
          // Let the browser manage update timing; we only react to updatefound.
          registration.addEventListener("updatefound", () => {
            const installingWorker = registration.installing;
            if (installingWorker) {
              installingWorker.addEventListener("statechange", () => {
                if (
                  installingWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  console.info(
                    "[UniUI] New version available — refresh to update.",
                  );
                }
              });
            }
          });
        })
        .catch((error) => {
          console.warn("[UniUI] Service_worker_registration_failed:", error);
        });

      // Try to subscribe to push after a short delay so the SW is ready
      setTimeout(() => tryPushSubscribe(), 3000);
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  // Also try to subscribe when the user returns to the tab after being away
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setTimeout(() => tryPushSubscribe(), 1500);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  return null;
}
